import React from 'react';
import { Calendar, Clock, Heart, Users, MessageCircle, Share2, BookOpen, AlertCircle, Star, Pin, Info } from 'lucide-react';
import { UnifiedHeader } from '../components/layout';
import { usePublicAnnouncements } from '../hooks/useAnnouncements';
import { AnnouncementType } from '../types/announcements';
import { MarkdownRenderer } from '../components/ui/MarkdownRenderer';
import { Button } from '../components/ui/Button';

const NewsPageHeritage: React.FC = () => {
  const { data: featuredData, isLoading: featuredLoading, error: featuredError } = usePublicAnnouncements({
    featured: true,
    limit: 3
  });

  const { data: recentData, isLoading: recentLoading, error: recentError } = usePublicAnnouncements({
    featured: false,
    limit: 6
  });

  const getTypeDisplay = (type: AnnouncementType) => {
    const typeConfig = {
      meeting: { icon: Users, label: 'Meeting' },
      birthday: { icon: Heart, label: 'Birthday' },
      event: { icon: Calendar, label: 'Event' },
      memorial: { icon: Heart, label: 'Memorial' },
      fundraiser: { icon: Heart, label: 'Fundraiser' },
      general: { icon: Info, label: 'General' }
    };
    return typeConfig[type] || typeConfig.general;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-surface-0">
      <UnifiedHeader currentPage="News" />

      {/* Hero Section */}
      <section className="bg-surface-1 border-b border-border mt-16 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xs uppercase tracking-wider text-text-tertiary mb-3">Latest News</p>
          <h1 className="text-4xl font-semibold text-text-primary mb-4">
            Family News & Updates
          </h1>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Stay connected with the latest happenings, celebrations, and important updates from the Redd Family Association.
          </p>
        </div>
      </section>

      {/* Featured News Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-semibold text-text-primary mb-2">
              Featured Stories
            </h2>
            <p className="text-text-secondary">
              The most important updates from our family association
            </p>
          </div>

          {/* Main Featured Article */}
          <div className="mb-12">
            {featuredLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4" />
                <p className="text-text-secondary">Loading featured announcements...</p>
              </div>
            ) : featuredError ? (
              <div className="text-center py-12">
                <AlertCircle className="w-10 h-10 text-error mx-auto mb-4" />
                <p className="text-error">Unable to load featured announcements</p>
              </div>
            ) : featuredData?.announcements && featuredData.announcements.length > 0 ? (
              featuredData.announcements.slice(0, 1).map((announcement) => {
                const typeDisplay = getTypeDisplay(announcement.type);
                const TypeIcon = typeDisplay.icon;

                return (
                  <div key={announcement.id} className="bg-surface-1 rounded-lg border border-border overflow-hidden">
                    <div className="md:flex">
                      <div className="md:w-1/3 bg-surface-2 flex items-center justify-center p-8">
                        <div className="text-center">
                          <TypeIcon className="w-12 h-12 text-brand mx-auto mb-3" />
                          <p className="text-xs uppercase tracking-wider text-text-tertiary font-medium">{typeDisplay.label}</p>
                        </div>
                      </div>
                      <div className="md:w-2/3 p-8">
                        <div className="flex items-center space-x-3 mb-4">
                          <span className="px-2.5 py-0.5 bg-brand text-white text-xs font-medium rounded-full">
                            {typeDisplay.label}
                          </span>
                          <span className="flex items-center text-text-tertiary text-sm">
                            <Calendar className="w-3.5 h-3.5 mr-1.5" />
                            {formatDate(announcement.publishedAt || announcement.createdAt)}
                          </span>
                          {announcement.isPinned && <Pin className="w-3.5 h-3.5 text-accent-warm fill-current" />}
                          {announcement.isFeatured && <Star className="w-3.5 h-3.5 text-accent-warm fill-current" />}
                        </div>

                        <h3 className="text-2xl font-semibold text-text-primary mb-4">
                          {announcement.title}
                        </h3>

                        <div className="mb-6">
                          <MarkdownRenderer
                            content={announcement.excerpt || announcement.content.substring(0, 200) + '...'}
                            proseColor="slate"
                            className="text-text-secondary leading-relaxed"
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-text-tertiary text-sm">Redd Family Association</span>
                          <Button variant="primary" size="sm">Read More</Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-12">
                <MessageCircle className="w-10 h-10 text-text-tertiary mx-auto mb-4" />
                <p className="text-text-secondary">No featured announcements available</p>
              </div>
            )}
          </div>

          {/* Secondary Featured Articles */}
          <div className="grid md:grid-cols-2 gap-6">
            {featuredData?.announcements && featuredData.announcements.length > 1 ? (
              featuredData.announcements.slice(1).map((announcement) => {
                const typeDisplay = getTypeDisplay(announcement.type);
                const TypeIcon = typeDisplay.icon;

                return (
                  <div key={announcement.id} className="bg-surface-1 rounded-lg border border-border overflow-hidden">
                    <div className="bg-surface-2 p-6 flex items-center justify-center">
                      <div className="text-center">
                        <TypeIcon className="w-8 h-8 text-brand mx-auto mb-2" />
                        <p className="text-xs text-text-tertiary uppercase tracking-wider">{typeDisplay.label}</p>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center space-x-3 mb-3">
                        <span className="px-2.5 py-0.5 bg-brand text-white text-xs font-medium rounded-full">
                          {typeDisplay.label}
                        </span>
                        <span className="flex items-center text-text-tertiary text-xs">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatDate(announcement.publishedAt || announcement.createdAt)}
                        </span>
                        {announcement.isPinned && <Pin className="w-3 h-3 text-accent-warm fill-current" />}
                      </div>

                      <h3 className="text-lg font-semibold text-text-primary mb-3">
                        {announcement.title}
                      </h3>

                      <div className="mb-4">
                        <MarkdownRenderer
                          content={announcement.excerpt || announcement.content.substring(0, 150) + '...'}
                          proseColor="slate"
                          className="text-sm text-text-secondary"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-text-tertiary text-sm">Redd Family Association</span>
                        <div className="flex space-x-1">
                          <button className="p-1.5 text-text-tertiary hover:text-brand transition-colors">
                            <Heart className="w-4 h-4" />
                          </button>
                          <button className="p-1.5 text-text-tertiary hover:text-brand transition-colors">
                            <Share2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : !featuredLoading && (
              <div className="col-span-2 text-center py-8">
                <p className="text-text-tertiary">No additional featured announcements</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Recent News Section */}
      <section className="py-16 bg-surface-1 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-semibold text-text-primary mb-2">
              Recent Updates
            </h2>
            <p className="text-text-secondary">
              Stay informed with our latest family news and community updates
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {recentLoading ? (
              Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="bg-white rounded-lg border border-border p-6 animate-pulse">
                  <div className="h-3 bg-surface-2 rounded mb-4 w-1/3" />
                  <div className="h-5 bg-surface-2 rounded mb-3" />
                  <div className="h-16 bg-surface-2 rounded mb-4" />
                  <div className="h-3 bg-surface-2 rounded w-1/2" />
                </div>
              ))
            ) : recentError ? (
              <div className="col-span-3 text-center py-8">
                <AlertCircle className="w-8 h-8 text-error mx-auto mb-4" />
                <p className="text-error">Unable to load recent announcements</p>
              </div>
            ) : recentData?.announcements && recentData.announcements.length > 0 ? (
              recentData.announcements.map((announcement) => {
                const typeDisplay = getTypeDisplay(announcement.type);
                const TypeIcon = typeDisplay.icon;

                return (
                  <div key={announcement.id} className="bg-white rounded-lg border border-border p-6 hover:shadow-sm transition-shadow">
                    <div className="flex items-center space-x-2 mb-3">
                      <span className="px-2 py-0.5 bg-surface-2 text-text-secondary text-xs font-medium rounded-full flex items-center space-x-1">
                        <TypeIcon className="w-3 h-3" />
                        <span>{typeDisplay.label}</span>
                      </span>
                      <span className="text-text-tertiary text-xs flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {formatDate(announcement.publishedAt || announcement.createdAt)}
                      </span>
                      {announcement.isPinned && <Pin className="w-3 h-3 text-accent-warm fill-current" />}
                    </div>

                    <h3 className="text-base font-semibold text-text-primary mb-2">
                      {announcement.title}
                    </h3>

                    <div className="mb-4">
                      <MarkdownRenderer
                        content={announcement.excerpt || announcement.content.substring(0, 120) + '...'}
                        proseColor="slate"
                        className="text-sm text-text-secondary"
                      />
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-border">
                      <span className="text-text-tertiary text-xs">Redd Family Association</span>
                      <div className="flex space-x-1">
                        <button className="p-1 text-text-tertiary hover:text-brand transition-colors">
                          <MessageCircle className="w-3.5 h-3.5" />
                        </button>
                        <button className="p-1 text-text-tertiary hover:text-brand transition-colors">
                          <Share2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-3 text-center py-12">
                <MessageCircle className="w-10 h-10 text-text-tertiary mx-auto mb-4" />
                <h3 className="text-base font-semibold text-text-primary mb-2">No recent announcements</h3>
                <p className="text-text-secondary text-sm">Check back soon for family updates and news.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-16">
        <div className="max-w-xl mx-auto px-4 text-center">
          <div className="bg-surface-1 rounded-lg border border-border p-8">
            <div className="w-12 h-12 bg-brand rounded-lg flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-6 h-6 text-white" />
            </div>

            <h2 className="text-xl font-semibold text-text-primary mb-3">
              Stay Connected with Family News
            </h2>

            <p className="text-text-secondary text-sm mb-6">
              Never miss an important family update, reunion announcement, or celebration.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 max-w-sm mx-auto mb-4">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-3 py-2.5 rounded-md border border-border bg-white text-sm placeholder:text-text-tertiary focus:border-brand focus:ring-2 focus:ring-brand/20 focus:outline-none"
              />
              <Button variant="primary" size="sm">Subscribe</Button>
            </div>

            <p className="text-xs text-text-tertiary">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-surface-1 border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-xl font-display font-semibold text-text-primary mb-2">
            The Redd Family Association
          </h3>
          <p className="text-text-tertiary text-sm mb-4 italic">
            A family that prays together, stays together
          </p>
          <p className="text-text-tertiary text-xs">
            &copy; {new Date().getFullYear()} The Redd Family Association. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default NewsPageHeritage;
